import styled from 'styled-components';
import { useRef, useEffect } from 'react';
import { subtitle2, colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';
import { useSelectContentEditorContext } from './contexts';
import { useContentTemplateSelectorKeyPress } from './hooks/useContentTemplateSelectorKeyPress';

export const DRAG_AND_DROP_TEMPLATE_SELECTOR_TEST_ID = 'drag-and-drop-template-selector';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const OptionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const OptionBlank = styled.div`
  display: inline-flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;

  align-content: space-around;
`;

const OptionWrapper = styled.div`
  display: inline-flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;

  align-content: space-around;
  border-radius: ${({ theme }) => theme.spacing.values.half};
  background: #f3f4f5;
  border: ${({ theme }) => `${theme.spacing.utils.pxToRem(3)} solid #fff`};

  :focus {
    outline: none;
    ${({ isSelected }: { isSelected: boolean }) =>
      !isSelected &&
      `
      border: ${pxToRem(1.5)} solid ${colors.grey50};
      margin: ${pxToRem(1.5)};
  `}
  }

  ${({ isSelected }: { isSelected: boolean }) =>
    isSelected &&
    `
    border: ${pxToRem(3)} solid ${colors.purple50};
  `}
`;

const OptionText = styled.span`
  ${subtitle2}
  margin: ${({ theme }) => `${theme.spacing.values.threeQuarter} 0`};
  padding: 0;
  text-align: center;
`;

const SvgWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.values.base};
`;

type ContentTemplateSelectorProps = {
  setSelectedTemplate: (arg: string) => void;
  selectedTemplate: string;
};

export const ContentTemplateSelector = ({
  setSelectedTemplate = noop,
  selectedTemplate,
  ...props
}: ContentTemplateSelectorProps) => {
  const { dragAndDropTemplateOptions } = useSelectContentEditorContext();
  const handleKeyPress = useContentTemplateSelectorKeyPress();

  const refs = useRef<HTMLDivElement[] | null[]>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  return (
    <Wrapper data-test={DRAG_AND_DROP_TEMPLATE_SELECTOR_TEST_ID} {...props}>
      <OptionContainer onKeyDown={(e) => handleKeyPress(e, refs)}>
        {dragAndDropTemplateOptions.map(({ id, label, svg }, index) => {
          if (!id) {
            return <OptionBlank key={`row-${index + 1}-item-${index + 1}`} />; // fill space to maintain grid appearance
          }

          return (
            <OptionWrapper
              key={id}
              tabIndex={0}
              data-test={`${id}-option`}
              isSelected={selectedTemplate === id}
              onClick={() => setSelectedTemplate(id)}
              ref={(ref) => {
                refs.current[index] = ref;
              }}
            >
              <SvgWrapper>{svg ? svg({}) : null}</SvgWrapper>
              <OptionText>{label}</OptionText>
            </OptionWrapper>
          );
        })}
      </OptionContainer>
    </Wrapper>
  );
};
